/**
 * <ch-moving-still> — Coastline's poster → clip media (snippets/ch-moving-still.liquid).
 *
 * The poster paints from the HTML. The clip waits inert in a <template>: nothing is fetched
 * until the box is at least a quarter on screen, motion is allowed, and a playback slot is
 * free. Then the <video> is cloned in (once per connection), played muted and inline, and
 * faded in over the poster when it is actually playing (CSS: .ch-moving-still--playing).
 * Off screen it pauses, rewinds to the poster frame and gives its slot back.
 *
 * Two ways to stay inside WCAG 2.2.2 (Pause, Stop, Hide):
 *
 * - Default, five seconds per visit: the clip loops for at most MAX_PLAY_MS each time the
 *   box comes on screen, then fades back to the still and frees its slot. Leaving the
 *   viewport and coming back starts a new visit. Motion that starts on its own and stops
 *   within five seconds needs no pause control.
 *
 * - Loop (`data-loop` plus the snippet's pause button): the clip keeps looping while it is
 *   on screen. The button pauses on the current frame — no jump back to the poster — and
 *   frees the slot; pressing it again resumes. The choice is kept for the session
 *   (sessionStorage) and shared by every looping still and every rotating slideshow, so a
 *   visitor who paused isn't re-animated on the next page: there the clip isn't even fetched
 *   until they press play.
 *   The button only shows while motion is possible (not under reduced motion, Save-Data,
 *   2G or a budget of 0, nor once the clip has failed to load). Without the button in the
 *   markup, `data-loop` is ignored.
 *
 * Motion budget: at most --ch-motion-budget things move at once (theme setting
 * ch_motion_budget, printed on <html> by snippets/ch-fonts.liquid; 2 if missing, never
 * more than 2 — docs/01-brand.md). On-screen instances that find the budget spent keep their
 * poster and try again whenever a slot is released.
 *
 * The motion ledger. The budget and the visitor's pause / play choice live in one object per
 * page, `motion`, exported for anything else that moves: <ch-slideshow> holds a slot while it
 * rotates and shares the pause, so a press on the hero's button or on the slideshow's acts on
 * both at once. The API:
 *   motion.claimSlot()      → true when a slot was taken; false under reduced motion,
 *                             Save-Data, 2G, a budget of 0, or with every slot in use
 *   motion.releaseSlot()    gives a claimed slot back and fires `release` (held things retry)
 *   motion.allowed          false when nothing may move at all (claimSlot would never succeed)
 *   motion.paused           the visitor's choice, kept for the session (sessionStorage
 *                           'ch-motion-paused')
 *   motion.setPaused(bool)  stores it and fires `pausechange`, synchronously inside the press
 *                           (a play the browser only allows from a user gesture still has one)
 *   motion.inUse            slots taken right now (for tests)
 * It is an EventTarget: listen for `release` and `pausechange`. The ledger rides on the
 * registered element class (customElements.get('ch-moving-still').motion), so a second copy
 * of this file — the same code under another URL, e.g. imported without Shopify's ?v= — finds
 * and exports the same object instead of starting a second count. No window global.
 *
 * Renditions: video_tag only offers Shopify's 1080p mp4. The template also carries the
 * video's JSON, which lists every rendition; before the clip mounts, the element swaps in
 * the largest one that fits the viewport — up to 720p on phones, 1080p above.
 */

const VISIBLE_RATIO = 0.25;
const DEFAULT_BUDGET = 2;
const MAX_BUDGET = 2;
const MAX_PLAY_MS = 5000;
const PLAYING_CLASS = 'ch-moving-still--playing';
const SLOW_CONNECTIONS = ['slow-2g', '2g'];
const PAUSED_KEY = 'ch-motion-paused';
// A rendition is named for its short side (720p = 1588 × 720 here, 720 × 1280 in portrait).
const MAX_RENDITION = 1080;
const MAX_RENDITION_PHONE = 720;

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
// Venue's phone layout ends at 767px (assets/base.css).
const phoneViewport = window.matchMedia('(max-width: 767px)');

// The visitor's pause / play choice, kept for the session. Storage can be blocked (private
// modes, embedded previews): the choice then lasts for this page.
function readPaused() {
  try {
    return window.sessionStorage.getItem(PAUSED_KEY) === '1';
  } catch {
    return false;
  }
}

function writePaused(paused) {
  try {
    if (paused) {
      window.sessionStorage.setItem(PAUSED_KEY, '1');
    } else {
      window.sessionStorage.removeItem(PAUSED_KEY);
    }
  } catch {
    // Not stored: the ledger still holds it for this page.
  }
}

function motionBudget() {
  const value = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--ch-motion-budget'), 10);
  return Number.isNaN(value) ? DEFAULT_BUDGET : Math.min(Math.max(value, 0), MAX_BUDGET);
}

// Still only: reduced motion, Save-Data, or a 2G-class connection.
function prefersStill() {
  const connection = navigator.connection;
  return reducedMotion.matches || Boolean(connection?.saveData) || SLOW_CONNECTIONS.includes(connection?.effectiveType);
}

// The page's motion budget and pause choice (see the header for the API).
class MotionLedger extends EventTarget {
  #inUse = 0;
  #paused = readPaused();

  constructor() {
    super();
    // Back from the back/forward cache: pick up a pause or play pressed on the page the
    // visitor is coming back from. Registered before any element's own pageshow listener, so
    // theirs already read the fresh value.
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      const paused = readPaused();
      if (paused === this.#paused) return;
      this.#paused = paused;
      this.dispatchEvent(new Event('pausechange'));
    });
  }

  get allowed() {
    return motionBudget() > 0 && !prefersStill();
  }

  get inUse() {
    return this.#inUse;
  }

  get paused() {
    return this.#paused;
  }

  claimSlot() {
    if (!this.allowed || this.#inUse >= motionBudget()) return false;
    this.#inUse += 1;
    return true;
  }

  releaseSlot() {
    this.#inUse = Math.max(this.#inUse - 1, 0);
    this.dispatchEvent(new Event('release'));
  }

  // Always announced, even when the value doesn't change: a press on a held clip's button
  // (paused is already false) is still the gesture that starts it.
  setPaused(paused) {
    this.#paused = Boolean(paused);
    writePaused(this.#paused);
    this.dispatchEvent(new Event('pausechange'));
  }
}

// One per page: a copy of this file evaluated before us already registered the element and
// its ledger. Anything else (an older build without one) gets a ledger of its own.
const registered = customElements.get('ch-moving-still')?.motion;
export const motion = typeof registered?.claimSlot === 'function' ? registered : new MotionLedger();

const pathOf = (url) => new URL(url, document.baseURI).pathname;

// The mp4 to play at this viewport, from the video JSON in the template: the largest
// rendition within the cap, or the smallest there is when they are all above it. Null when
// the data is missing or unreadable — the clip then plays the source video_tag printed.
function pickRendition(template) {
  let sources;
  try {
    sources = JSON.parse(template.content.querySelector('script[type="application/json"]')?.textContent ?? 'null')?.sources;
  } catch {
    return null;
  }
  if (!Array.isArray(sources)) return null;

  const cap = phoneViewport.matches ? MAX_RENDITION_PHONE : MAX_RENDITION;
  const renditions = sources
    .filter((source) => source?.format === 'mp4' && source.url)
    // A rendition without a size sorts last, so it is only ever the fallback.
    .map((source) => ({ url: source.url, type: source.mime_type, size: Math.min(source.width, source.height) || Infinity }))
    .sort((a, b) => a.size - b.size);

  return renditions.filter((rendition) => rendition.size <= cap).pop() ?? renditions[0] ?? null;
}

class MovingStill extends HTMLElement {
  // Where a later copy of this module (and anything holding the class) finds the ledger.
  static motion = motion;

  #observer = null;
  #video = null;
  // The pause button, when this instance loops. No button, no loop: the five-second visit.
  #control = null;
  #isVisible = false;
  #hasSlot = false;
  #hasFailed = false;
  // Set when this visit's five seconds are used up; cleared when the box leaves the screen.
  #hasPlayedThisVisit = false;
  // Loop only: the browser refused to start the clip or paused it itself. Nothing restarts
  // it on its own from here, so the button offers play and a press (a user gesture) does it.
  #isHeld = false;
  #playTimer = 0;

  connectedCallback() {
    // Poster-only instance (no <template>), or already set up.
    if (this.#observer || !this.querySelector(':scope > template')) return;

    // A node cloned from a live instance (e.g. a looping carousel) arrives with a clip and
    // a playing class it doesn't own: start from the poster.
    this.#reset();

    this.#control = this.hasAttribute('data-loop') ? this.querySelector(':scope > .ch-moving-still__control') : null;
    if (this.#control) {
      this.#control.addEventListener('click', this.#onControlClick);
      motion.addEventListener('pausechange', this.#onPreferenceChange);
      document.addEventListener('visibilitychange', this.#onVisibilityChange);
      this.#syncControl();
    }

    this.#observer = new IntersectionObserver(this.#onIntersect, { threshold: VISIBLE_RATIO });
    this.#observer.observe(this);
    reducedMotion.addEventListener('change', this.#onMotionPreferenceChange);
    motion.addEventListener('release', this.#onSlotReleased);
    window.addEventListener('pageshow', this.#onPageShow);
  }

  // Theme editor re-renders replace sections wholesale: leave no slot, listener or clip behind.
  disconnectedCallback() {
    if (!this.#observer) return;

    this.#observer.disconnect();
    this.#observer = null;
    reducedMotion.removeEventListener('change', this.#onMotionPreferenceChange);
    motion.removeEventListener('release', this.#onSlotReleased);
    window.removeEventListener('pageshow', this.#onPageShow);
    if (this.#control) {
      this.#control.removeEventListener('click', this.#onControlClick);
      motion.removeEventListener('pausechange', this.#onPreferenceChange);
      document.removeEventListener('visibilitychange', this.#onVisibilityChange);
      // Back to the markup's state: a copy of this node starts hidden, as the snippet prints it.
      this.#control.hidden = true;
      this.#control = null;
    }
    this.#isVisible = false;
    this.#hasPlayedThisVisit = false;
    this.#isHeld = false;
    this.#reset();
  }

  #onIntersect = (entries) => {
    const entry = entries[entries.length - 1];
    this.#isVisible = entry.isIntersecting && entry.intersectionRatio >= VISIBLE_RATIO;

    if (this.#isVisible) {
      this.#play();
    } else {
      this.#hasPlayedThisVisit = false;
      // A looping clip the visitor paused keeps its frame for when they scroll back.
      if (!this.#control || this.#hasSlot) this.#stop();
    }
  };

  #onMotionPreferenceChange = () => {
    if (reducedMotion.matches) {
      this.#stop();
    } else {
      this.#play();
    }
    this.#syncControl();
  };

  #onSlotReleased = () => this.#play();

  // Back from the back/forward cache: the browser paused the clip (and #onPaused put the
  // poster back), so start a fresh visit if the box is still on screen. A looping still
  // follows a pause or play pressed on the page the visitor is coming back from (the ledger
  // has re-read it by now).
  #onPageShow = (event) => {
    if (!event.persisted) return;
    this.#hasPlayedThisVisit = false;
    if (this.#control) {
      this.#isHeld = false;
      if (motion.paused) this.#freeze();
      this.#syncControl();
    }
    this.#play();
  };

  // Loop only. The tab is showing again: if the browser paused the clip meanwhile, try again.
  #onVisibilityChange = () => {
    if (document.hidden || !this.#isHeld) return;
    this.#isHeld = false;
    this.#syncControl();
    this.#play();
  };

  // Loop only. The label says what a press will do, so it is also the state to flip.
  #onControlClick = () => {
    const isPaused = motion.paused || this.#isHeld;
    this.#isHeld = false;
    motion.setPaused(!isPaused);
  };

  // Loop only. Runs for every looping still on the page, synchronously inside the press —
  // here or on the slideshow's button — so a play that needs a user gesture (autoplay was
  // refused) still has one.
  #onPreferenceChange = () => {
    if (motion.paused) {
      this.#freeze();
    } else {
      this.#isHeld = false;
      this.#play();
    }
    this.#syncControl();
  };

  // `playing` can already be queued when #stop pauses: only fade in while we hold a slot.
  // The visit's clock starts on the first frame actually shown; a loop has no clock.
  #onPlaying = () => {
    if (!this.#hasSlot) return;
    this.classList.add(PLAYING_CLASS);
    if (!this.#control && !this.#playTimer) this.#playTimer = window.setTimeout(this.#settle, MAX_PLAY_MS);
  };

  // A pause we didn't ask for (the browser suspending media, a system interruption) must not
  // keep a slot with nothing moving. Our own pauses release the slot first, so they no-op
  // here, as does a stale event that arrives after the clip is playing again.
  // Five-second visit: treated as the end of the visit, so the release below doesn't restart
  // it straight away. Loop: held on the frame it stopped at, with the button offering play.
  #onPaused = () => {
    if (!this.#hasSlot || !this.#video?.paused || this.#video.ended) return;
    if (this.#control) {
      this.#hold();
    } else {
      this.#hasPlayedThisVisit = true;
      this.#stop();
    }
  };

  // The clip can't be loaded, or broke while playing. A <video> with <source> children
  // never rejects play() over this — it waits for a source that isn't coming — so the error
  // events are the only signal. Each failing <source> fires one (caught here on the way
  // down to it); the clip is lost once none is left to try, or the <video> reports an error
  // of its own.
  #onError = (event) => {
    const video = event.currentTarget;
    if (video !== this.#video) return;
    if (video.error || video.networkState === HTMLMediaElement.NETWORK_NO_SOURCE) this.#fail();
  };

  // Five seconds are up: back to the still for the rest of this visit.
  #settle = () => {
    this.#playTimer = 0;
    this.#hasPlayedThisVisit = true;
    this.#stop();
  };

  #play() {
    // isConnected: an observer entry queued before disconnect must not claim a slot.
    if (!this.isConnected || !this.#isVisible || this.#hasSlot || this.#hasFailed) return;
    if (this.#control ? motion.paused || this.#isHeld : this.#hasPlayedThisVisit) return;
    // Motion off (reduced motion, Save-Data, 2G, a budget of 0) or the budget spent: hold the
    // poster until the next release.
    if (!motion.claimSlot()) return;
    this.#hasSlot = true;

    // A template without a <video> can't play: #fail hands the slot straight back.
    const video = this.#video ?? this.#mountVideo();
    if (!video) {
      this.#fail();
      return;
    }

    video.play().catch((error) => {
      // pause() landed before play() settled (scrolled away, reduced motion, paused, removed):
      // #stop, #freeze or #reset already released the slot.
      if (error.name === 'AbortError' || video !== this.#video) return;

      // Autoplay refused (e.g. iOS Low Power Mode). A looping still keeps its button, which
      // now offers play: a press is the user gesture the browser wants.
      if (this.#control && error.name === 'NotAllowedError') {
        this.#hold();
        return;
      }

      // Refused with no button to press, or a source the browser can't play: poster for
      // good, and the slot goes to someone who can use it. (A clip that fails to load never
      // gets here: see #onError.)
      this.#fail();
    });
  }

  // Back to the poster: pause, rewind to the frame the poster was exported from, free the slot.
  #stop() {
    window.clearTimeout(this.#playTimer);
    this.#playTimer = 0;
    // Release first: the pause below queues a `pause` event that #onPaused must ignore.
    const hadSlot = this.#hasSlot;
    this.#hasSlot = false;
    if (this.#video) {
      this.#video.pause();
      this.#video.currentTime = 0;
    }
    this.classList.remove(PLAYING_CLASS);
    if (hadSlot) this.#announceRelease();
  }

  // Loop only. Pause where it is: the frame stays on screen, the slot goes back. Callers
  // set motion.paused or #isHeld first, so the release doesn't start this clip again.
  #freeze() {
    const hadSlot = this.#hasSlot;
    this.#hasSlot = false;
    this.#video?.pause();
    if (hadSlot) this.#announceRelease();
  }

  // Loop only. The browser won't play the clip, or stopped it: see #isHeld.
  #hold() {
    this.#isHeld = true;
    this.#freeze();
    this.#syncControl();
  }

  // This clip can't play at all: poster for good, its slot freed, and no button for
  // something that can't move.
  #fail() {
    this.#hasFailed = true;
    this.#reset();
    this.#syncControl();
  }

  // Poster only, clip removed: used on disconnect, on playback failure, and for stale clones.
  #reset() {
    this.#stop();
    this.querySelectorAll(':scope > video').forEach((video) => video.remove());
    this.#video = null;
  }

  // Called only by #stop and #freeze, after they have dropped this instance's claim.
  #announceRelease() {
    motion.releaseSlot();
  }

  // Loop only. Show the button while this clip could move, and make it say what a press does.
  #syncControl() {
    const control = this.#control;
    if (!control) return;

    const isPaused = motion.paused || this.#isHeld;
    control.hidden = this.#hasFailed || !motion.allowed;
    control.toggleAttribute('data-paused', isPaused);
    control.setAttribute('aria-label', isPaused ? control.dataset.labelPlay : control.dataset.labelPause);
  }

  #mountVideo() {
    // Cloned out of the template's inert document, the <video> fetches nothing until it is
    // appended, so the parts we don't need can go first: our <img> is the poster, so
    // video_tag's poster attribute and fallback <img> would only download it twice.
    const template = this.querySelector(':scope > template');
    const source = template?.content.querySelector('video');
    if (!source) return null;

    const video = source.cloneNode(true);
    video.removeAttribute('poster');
    video.querySelector('img')?.remove();

    // The rendition for this viewport. When it is the file video_tag already points at, that
    // <source> stays exactly as Shopify printed it.
    const rendition = pickRendition(template);
    if (rendition) {
      const printed = [...video.querySelectorAll('source')];
      const match = printed.find((element) => pathOf(element.getAttribute('src')) === pathOf(rendition.url));
      printed.forEach((element) => {
        if (element !== match) element.remove();
      });
      if (!match) {
        const chosen = document.createElement('source');
        chosen.src = rendition.url;
        chosen.type = rendition.type ?? 'video/mp4';
        video.prepend(chosen);
      }
    }

    // The poster's alt already describes the scene; the clip is the same picture moving.
    video.setAttribute('aria-hidden', 'true');
    video.addEventListener('playing', this.#onPlaying);
    video.addEventListener('pause', this.#onPaused);
    // Capture: a <source>'s error event doesn't bubble up to the <video>.
    video.addEventListener('error', this.#onError, true);

    this.append(video);
    video.muted = true;
    video.playsInline = true;
    this.#video = video;
    return video;
  }
}

if (!customElements.get('ch-moving-still')) {
  customElements.define('ch-moving-still', MovingStill);
}
